import React, { useState } from 'react';
import { stylesheet } from 'astroturf/react';
import DOMPurify from 'dompurify';
import { Dropdown } from 'js/design_system/forms/Dropdown';
import { PopoverWidthMode } from 'js/design_system/Popover';
import trackClick from 'js/utils/trackClick';
import { loadRecordHit } from 'js/utils/loadRecordHit';

type AiTranslationDropdownProps = {
  abstract: string;
  abstractElement: HTMLElement;
  aiTranslations: Record<string, string>;
  sectionTitle: string;
  titleElement: HTMLElement;
  humanizedLanguageKeys: Record<string, string>;
  workId: number;
};

const styles = stylesheet`
  .aiTranslationDropdown button {
    border: 0;
    color: var(--primary-brand, #0645B1);
    padding: 0;
  }
`;

/**
 * This is used in the SAP to add a toggleable dropdown, adding AI-translated abstracts.
 * When used, this will replace the exact text.
 * @param props.abstract - the default, untranslated abstract text
 * @param props.abstractElement - the HTML element which will be modified with the translations
 * @param props.aiTranslations - contains the translations, with the format: { language_key: "translations" }
 * @param props.sectionTitle - the default abstract section title when the untranslated abstract is shown
 * @param props.titleElement - HTML element for the section title, used to change "Abstract" to "AI-translated Abstract"
 * @returns
 */
export const AiTranslationDropdown = (props: AiTranslationDropdownProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);

  const { humanizedLanguageKeys } = props;

  const languageKeys = Object.keys(props.aiTranslations);

  const handleChange = (selectedOption: string | null) => {
    setSelectedLanguage(selectedOption);
    props.abstractElement.toggleAttribute(
      'data-sap-translated-abstract',
      selectedOption != null,
    );
    props.abstractElement.innerHTML = DOMPurify.sanitize(
      selectedOption == null
        ? props.abstract
        : props.aiTranslations[selectedOption],
    );
    props.titleElement.innerText =
      selectedOption == null ? props.sectionTitle : 'AI-translated Abstract';

    if (selectedOption) {
      void loadRecordHit()
        .catch(() => undefined)
        .then(() => {
          trackClick({
            source: 'sap_abstract_translation_select',
            entityId: props.workId,
            extra: { language: selectedOption },
          });
        });
    }
  };

  return (
    <Dropdown
      placeholder="Translate Abstract using AI"
      value={selectedLanguage}
      options={[null, ...languageKeys]}
      renderOption={(option) =>
        option != null ? humanizedLanguageKeys[option] : 'English (Original)'
      }
      onChange={handleChange}
      renderValue={(value) =>
        value != null
          ? humanizedLanguageKeys[value]
          : 'Translate Abstract using AI'
      }
      className={styles.aiTranslationDropdown}
      popoverProps={{ widthMode: PopoverWidthMode.Content }}
    />
  );
};
